import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { cardapioService } from '../../services';
import { formatCurrency } from '../../utils/format';
import './MesaCardapio.css';

export default function MesaCardapio() {
  const { numero } = useParams();
  const [cardapio, setCardapio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categoriaAtiva, setCategoriaAtiva] = useState(0);
  const [carrinho, setCarrinho] = useState([]);
  const [tela, setTela] = useState('cardapio'); // cardapio | pedido | sucesso
  const [nomeCliente, setNomeCliente] = useState('');
  const [obs, setObs] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [numeroPedido, setNumeroPedido] = useState(null);

  useEffect(() => {
    cardapioService.listar()
      .then(res => setCardapio(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const adicionarItem = (produto) => {
    if (produto.sem_estoque) return;
    setCarrinho(prev => {
      const ex = prev.find(i => i.produto_id === produto.id);
      if (ex) return prev.map(i => i.produto_id === produto.id ? { ...i, quantidade: i.quantidade + 1 } : i);
      return [...prev, { produto_id: produto.id, nome: produto.nome, emoji: produto.emoji || '🥟', preco: parseFloat(produto.preco), quantidade: 1 }];
    });
  };

  const removerItem = (produto_id) => {
    setCarrinho(prev => {
      const item = prev.find(i => i.produto_id === produto_id);
      if (!item) return prev;
      if (item.quantidade > 1) return prev.map(i => i.produto_id === produto_id ? { ...i, quantidade: i.quantidade - 1 } : i);
      return prev.filter(i => i.produto_id !== produto_id);
    });
  };

  const total = carrinho.reduce((s, i) => s + i.preco * i.quantidade, 0);
  const qtd = carrinho.reduce((s, i) => s + i.quantidade, 0);

  const enviarPedido = async () => {
    if (!nomeCliente.trim()) { alert('Informe seu nome'); return; }
    if (carrinho.length === 0) { alert('Adicione itens ao pedido'); return; }
    setEnviando(true);
    try {
      const { data } = await cardapioService.mesaPedido({
        mesa_numero: parseInt(numero),
        nome_cliente: nomeCliente.trim(),
        itens: carrinho.map(i => ({ produto_id: i.produto_id, quantidade: i.quantidade })),
        observacoes: obs || null,
      });
      setNumeroPedido(data.numero);
      setCarrinho([]);
      setTela('sucesso');
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao enviar pedido. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  if (loading) {
    return (
      <div className="mc-loading">
        <div className="spinner"></div>
        <p>Carregando cardápio...</p>
      </div>
    );
  }

  if (!cardapio) {
    return <div className="mc-loading"><p>Erro ao carregar cardápio.</p></div>;
  }

  const categorias = cardapio.categorias || [];

  /* ─── Sucesso ─── */
  if (tela === 'sucesso') {
    return (
      <div className="mc-sucesso">
        <div className="mc-sucesso-content">
          <div className="mc-sucesso-icone">✅</div>
          <h2>Pedido Enviado!</h2>
          <p>Pedido #{numeroPedido}</p>
          <p>Mesa {numero}</p>
          <p className="mc-sucesso-info">Seu pedido foi enviado para a cozinha. Em breve um de nossos atendentes irá até você!</p>
          <button className="mc-btn-primary" onClick={() => setTela('cardapio')}>
            Fazer Outro Pedido
          </button>
        </div>
      </div>
    );
  }

  /* ─── Confirmação ─── */
  if (tela === 'pedido') {
    return (
      <div className="mc-page">
        <div className="mc-header">
          <button className="mc-back" onClick={() => setTela('cardapio')}>← Voltar</button>
          <h1>Confirmar Pedido</h1>
          <span>Mesa {numero}</span>
        </div>

        <div className="mc-confirm-content">
          <div className="mc-form-group">
            <label>Seu nome *</label>
            <input
              type="text"
              placeholder="Como prefere ser chamado?"
              value={nomeCliente}
              onChange={e => setNomeCliente(e.target.value)}
              autoFocus
            />
          </div>

          <div className="mc-itens-confirm">
            {carrinho.map(item => (
              <div key={item.produto_id} className="mc-item-confirm">
                <span>{item.emoji} {item.nome}</span>
                <div className="mc-item-ctrl">
                  <button onClick={() => removerItem(item.produto_id)}>−</button>
                  <span>{item.quantidade}</span>
                  <button onClick={() => adicionarItem({ id: item.produto_id, nome: item.nome, emoji: item.emoji, preco: item.preco })}>+</button>
                </div>
                <span className="mc-item-total">{formatCurrency(item.preco * item.quantidade)}</span>
              </div>
            ))}
          </div>

          <div className="mc-total-row">
            <span>Total estimado</span>
            <strong>{formatCurrency(total)}</strong>
          </div>

          <div className="mc-form-group">
            <label>Observações (opcional)</label>
            <textarea
              placeholder="Alguma restrição alimentar ou pedido especial?"
              value={obs}
              onChange={e => setObs(e.target.value)}
              rows={2}
            />
          </div>

          <p className="mc-aviso">* O pagamento é realizado na mesa com o atendente.</p>

          <button
            className="mc-btn-primary"
            onClick={enviarPedido}
            disabled={enviando || !nomeCliente.trim()}
          >
            {enviando ? 'Enviando...' : '✅ Enviar para Cozinha'}
          </button>
        </div>
      </div>
    );
  }

  /* ─── Cardápio ─── */
  const produtosDaCategoria = categorias[categoriaAtiva]?.produtos || [];

  return (
    <div className="mc-page">
      <div className="mc-header">
        <div className="mc-header-info">
          <h1>🥟 Parada Predileta</h1>
          <span>Mesa {numero}</span>
        </div>
      </div>

      <div className="mc-cats">
        {categorias.map((cat, i) => (
          <button
            key={cat.nome}
            className={`mc-cat-btn ${categoriaAtiva === i ? 'active' : ''}`}
            onClick={() => setCategoriaAtiva(i)}
          >
            {cat.emoji} {cat.nome}
          </button>
        ))}
      </div>

      <div className="mc-produtos-list">
        {produtosDaCategoria.map(p => {
          const noCarrinho = carrinho.find(i => i.produto_id === p.id);
          return (
            <div key={p.id} className={`mc-produto-item${p.sem_estoque ? ' produto-esgotado' : ''}`}>
              <div className="mc-prod-emoji">{p.emoji || '🥟'}</div>
              <div className="mc-prod-info">
                <div className="mc-prod-nome">{p.nome}</div>
                {p.descricao && <div className="mc-prod-desc">{p.descricao}</div>}
                <div className="mc-prod-preco">{formatCurrency(p.preco)}</div>
              </div>
              <div className="mc-prod-ctrl">
                {p.sem_estoque ? (
                  <span className="produto-esgotado-badge">Esgotado</span>
                ) : (
                  <>
                    {noCarrinho && (
                      <>
                        <button className="mc-qty-btn" onClick={() => removerItem(p.id)}>−</button>
                        <span className="mc-qty-num">{noCarrinho.quantidade}</span>
                      </>
                    )}
                    <button className="mc-add-btn" onClick={() => adicionarItem(p)}>+</button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {qtd > 0 && (
        <button className="mc-btn-float" onClick={() => setTela('pedido')}>
          🛒 {qtd} {qtd === 1 ? 'item' : 'itens'} — {formatCurrency(total)}
        </button>
      )}
    </div>
  );
}
